import "react-phone-number-input/style.css";

import { invalidateProfileQuery, patchProfileQueryData } from "@/features/profile/api/selectProfile";
import { clearOnboardingContactSkip } from "@/features/onboarding/utils/onboardingContactSkip";
import { supabaseClient } from "@/lib/supabase";
import {
    OnboardingStepFooter,
    type OnboardingTertiaryAction,
} from "@/features/onboarding/components/OnboardingStepFooter";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { Form } from "@patient-watch/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { toast } from "@patient-watch/ui/sonner";
import * as z from "zod";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";

const formSchema = z.object({
    phone: z
        .string()
        .min(1, "Phone number required")
        .refine((value) => isPossiblePhoneNumber(value), "Not a valid phone number"),
});

interface Props {
    userId: string;
    phone: string | null;
    onSuccess?: () => void;
    onBack?: () => void;
    isDialog?: boolean;
    primaryLabel?: string;
    tertiary?: OnboardingTertiaryAction;
}
const PhoneForm = (props: Props) => {
    const copy = useProfileCopy();
    const toastCopy = useToastCopy();
    const { locale } = useAppLocale();
    type FormSchemaType = z.infer<typeof formSchema>;

    const form = useForm<FormSchemaType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            phone: "",
        },
    });

    useEffect(() => {
        const raw = props.phone?.trim() ?? "";
        form.setValue("phone", raw.startsWith("+") ? raw : raw ? `+${raw}` : "");
    }, [props.phone, form]);

    const queryClient = useQueryClient();

    const updatePhoneMutation = useMutation({
        mutationFn: async ({ user_id, new_phone }: { user_id: string; new_phone: string }) => {
            const { data, error } = await supabaseClient.functions.invoke("public-update-user-phone", {
                body: { user_id, new_phone },
            });
            if (error) throw error;
            if (data && typeof data === "object" && "error" in data && data.error) {
                throw new Error(String(data.error));
            }
            return data;
        },
        onSuccess: (_data, variables) => {
            toast.success(toastCopy.profileUpdatedSuccess);
            patchProfileQueryData(queryClient, props.userId, { phone: variables.new_phone });
            void clearOnboardingContactSkip(props.userId, "phone")
                .then((userMetaData) => {
                    patchProfileQueryData(queryClient, props.userId, { user_meta_data: userMetaData });
                })
                .catch(() => undefined);
            void invalidateProfileQuery(queryClient, props.userId);
            void queryClient.invalidateQueries({ queryKey: ["invited-users"] });
            void queryClient.invalidateQueries({ queryKey: ["team-members"] });
            void queryClient.invalidateQueries({ queryKey: ["profiles"] });
            props.onSuccess?.();
        },
        onError: (error: Error) => {
            toast.error(error.message || toastCopy.failedUpdatePhone);
        },
    });

    const onSubmit: SubmitHandler<FormSchemaType> = async (formData) => {
        updatePhoneMutation.mutate({ user_id: props.userId, new_phone: formData.phone });
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="form">
                <fc.Phone autoFocus={true} label={copy.phone} locale={locale} />
                {props.isDialog ? (
                    <fc.SaveButton isDialog={props.isDialog} />
                ) : (
                    <OnboardingStepFooter
                        onBack={props.onBack}
                        disabled={updatePhoneMutation.isPending}
                        isLoading={updatePhoneMutation.isPending}
                        primaryLabel={props.primaryLabel}
                        tertiary={props.tertiary}
                    />
                )}
            </form>
        </Form>
    );
};

export default PhoneForm;
